import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { AppShell } from "@/components/layout/app-shell";
import { AdminRoute, ProtectedRoute } from "@/components/layout/protected-route";
import { ROTA_INICIAL } from "@/modules/dashboard/navegacao";
import LoginPage from "@/pages/auth/login";
import CadastroPage from "@/pages/auth/cadastro";
import {
  AguardandoAprovacao,
  NaoEncontrado,
  SemPermissao,
  SemProjeto,
} from "@/pages/estados";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  Rotas
 *
 *  Cada módulo é carregado com `lazy()`. Este é o code splitting LEGÍTIMO deste
 *  projeto: uma grelha de 60 colunas é o seu próprio chunk e só é descarregada
 *  quando o utilizador entra no módulo.
 *
 *  É também o substituto do `manualChunks`, que está proibido em
 *  `vite.config.ts` — ver o comentário-barreira lá para o incidente que motivou
 *  a proibição.
 *
 *  Os slugs são em pt-BR e coincidem com a chave usada em
 *  `user_module_access.module`. Manter os dois iguais evita uma tabela de
 *  tradução entre o que a rota diz e o que a RLS verifica.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const MODULO_ROTAS = [
  "cronograma",
  "requisitos",
  "legados",
  "dados",
  "backlog",
  "gaps",
  "parking-lot",
  "riscos-issues",
  "change-requests",
  "ricefws",
  "configuracoes",
  "bugs",
  "workshops",
  "decisoes-chave",
  "dashboard",
] as const;

const CARREGADORES: Record<(typeof MODULO_ROTAS)[number], () => Promise<any>> = {
  cronograma: () => import("@/modules/cronograma"),
  requisitos: () => import("@/modules/requisitos"),
  legados: () => import("@/modules/legados"),
  dados: () => import("@/modules/dados"),
  backlog: () => import("@/modules/backlog"),
  gaps: () => import("@/modules/gaps"),
  "parking-lot": () => import("@/modules/parking-lot"),
  "riscos-issues": () => import("@/modules/riscos-issues"),
  "change-requests": () => import("@/modules/change-requests"),
  ricefws: () => import("@/modules/ricefws"),
  configuracoes: () => import("@/modules/configuracoes"),
  bugs: () => import("@/modules/bugs"),
  workshops: () => import("@/modules/workshops"),
  "decisoes-chave": () => import("@/modules/decisoes-chave"),
  dashboard: () => import("@/modules/dashboard"),
};

// Vistas Kanban (PL-001). Filhas da rota do módulo, e não rotas soltas: assim
// `/ricefws/kanban` fica debaixo da MESMA guarda de leitura que a grelha, sem
// uma segunda lista de permissões por onde uma delas pudesse escapar.
const KANBAN: Partial<Record<(typeof MODULO_ROTAS)[number], () => Promise<any>>> = {
  ricefws: () => import("@/modules/ricefws/kanban"),
  configuracoes: () => import("@/modules/configuracoes/kanban"),
};

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/cadastro", element: <CadastroPage /> },
  { path: "/aguardando-aprovacao", element: <AguardandoAprovacao /> },
  { path: "/sem-projeto", element: <SemProjeto /> },
  { path: "/sem-permissao", element: <SemPermissao /> },

  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/cronograma" replace /> },
          // Cada módulo tem a sua própria guarda de permissão, para que o
          // redireccionamento para /sem-permissao seja por módulo e não global.
          ...MODULO_ROTAS.filter((slug) => slug !== "dashboard").map((slug) => ({
            path: slug,
            element: <ProtectedRoute modulo={slug} />,
            children: [
              { index: true, Component: lazy(CARREGADORES[slug]) },
              ...(KANBAN[slug] ? [{ path: "kanban", Component: lazy(KANBAN[slug]) }] : []),
            ],
          })),

          // O dashboard é o único módulo com ecrãs dentro de si. A casca traz a
          // barra de pastilhas e o fundo próprio; os dezasseis painéis entram
          // no <Outlet>. `/dashboard` sozinho não é destino — a barra precisa
          // de saber em que grupo está.
          {
            path: "dashboard",
            element: <ProtectedRoute modulo="dashboard" />,
            children: [
              {
                Component: lazy(() => import("@/modules/dashboard")),
                children: [
                  { index: true, element: <Navigate to={ROTA_INICIAL} replace /> },
                  {
                    path: ":grupo/:pagina",
                    Component: lazy(() => import("@/modules/dashboard/pagina")),
                  },
                ],
              },
            ],
          },

          // Administração. `AdminRoute` exige o papel de admin — sem isto, a
          // proteção seria só a sidebar esconder o link, e um URL direto
          // abria o ecrã. (As mutações continuam a ser recusadas pela RLS,
          // mas mostrar a lista de utilizadores a quem não é admin já seria
          // uma fuga.)
          // Projetos e Utilizadores são só de Admin/Master. Catálogos tem a
          // sua própria guarda, que deixa entrar o Gestor (do seu projeto) —
          // Sprint VI. Por isso vive numa rota irmã, não debaixo do mesmo
          // `AdminRoute` só-admin.
          {
            path: "admin",
            element: <AdminRoute />,
            children: [
              { index: true, element: <Navigate to="/admin/usuarios" replace /> },
              { path: "usuarios", Component: lazy(() => import("@/pages/admin/usuarios")) },
              { path: "projetos", Component: lazy(() => import("@/pages/admin/projetos")) },
            ],
          },
          {
            path: "admin/catalogos",
            element: <AdminRoute papeis={["gestor"]} />,
            children: [
              { index: true, Component: lazy(() => import("@/pages/admin/catalogos")) },
            ],
          },
        ],
      },
    ],
  },

  { path: "*", element: <NaoEncontrado /> },
]);
